import React from "react";
import { Link } from "react-router-dom";
import { ASSETS } from "@/lib/brand";
import Seo from "@/components/Seo";
import Pillars from "@/components/Pillars";

const CHAPTERS = [
  {
    num: "01",
    title: "Origins",
    paragraphs: [
      "Miguel Santana was born on October 1, 1987, in Santo Domingo, Dominican Republic, alongside his identical twin brother, Manuel Santana.",
      "Less than two years later, their family immigrated to New York City in search of greater opportunity and a better future.",
      "Growing up in one of the most influential and diverse cities in the world exposed Miguel to countless cultures, perspectives, and possibilities. Surrounded by entrepreneurship, music, sports, and the relentless energy of city life, he developed an early fascination with achievement, self-improvement, and the pursuit of success.",
      "From an early age, Miguel believed that where a person begins does not determine where they can ultimately go.",
    ],
  },
  {
    num: "02",
    title: "The Music Years",
    paragraphs: [
      "Like many young men growing up during the rise of hip-hop culture, Miguel dreamed of building a career in music.",
      "Alongside his brother, he spent years writing songs, recording music, performing, and pursuing opportunities in the entertainment industry.",
      "Music provided an outlet for creativity and self-expression. It taught discipline, resilience, and the importance of believing in a vision long before others could see it.",
      "Although the music career never developed exactly as planned, Miguel eventually came to view those experiences not as failures, but as preparation.",
      "The lessons remained even when the dream evolved.",
    ],
  },
  {
    num: "03",
    title: "Atlanta and Self-Discovery",
    paragraphs: [
      "After graduating from high school, Miguel relocated to Atlanta.",
      "While Atlanta exposed him to entertainment, nightlife, and new opportunities, the experience was ultimately less about nightlife and more about self-discovery.",
      "It was a period of growth, independence, and reflection.",
      "During those years, Miguel worked a variety of jobs, navigated challenges, built relationships, and began asking larger questions about purpose, success, and the future he wanted to create for himself.",
      "Atlanta became an important chapter — not because it provided all the answers, but because it forced him to begin searching for them.",
    ],
  },
  {
    num: "04",
    title: "The Boston Chapter",
    paragraphs: [
      "In 2011, Miguel relocated back to the Northeast and settled in the Boston area.",
      "The move marked another major turning point.",
      "Over the years, Miguel worked in security, asset protection, hospitality, nightlife, and event operations. These experiences exposed him to people from every background imaginable.",
      "Working closely with the public gave him a unique perspective on human behavior, communication, relationships, ambition, success, and failure.",
      "Night after night, year after year, he observed thousands of interactions unfold in real time.",
      "The experience taught him one of the most valuable lessons of his life:",
    ],
    pullQuote: "People's stories are often far more complicated than they appear on the surface.",
  },
  {
    num: "05",
    title: "Setbacks, Redirection, and Perspective",
    paragraphs: [
      "Like many entrepreneurs and creators, Miguel's journey was rarely linear.",
      "Business ideas were delayed. Projects were postponed. Partnerships fell apart. Financial challenges created obstacles. Personal setbacks forced him to pause and regroup more than once.",
      "Yet with every setback came a new lesson.",
      "What initially felt like failures eventually revealed themselves as redirections.",
      "Over time, Miguel began to realize that success is rarely determined by how many obstacles a person encounters.",
      "It is determined by whether they continue moving forward despite them.",
    ],
  },
  {
    num: "06",
    title: "Building the Vision",
    paragraphs: [
      "For years, Miguel developed ideas that extended far beyond a single business or project.",
      "He envisioned media companies. Technology platforms. Books. Podcasts. Consumer brands. Financial services. Sports ventures. Community-focused initiatives.",
      "While many of these ideas remained unfinished for years due to circumstances, responsibilities, and setbacks, the vision itself never disappeared.",
      "Instead, it continued to evolve.",
      "What began as scattered ideas gradually became a larger mission centered around creating opportunities, building meaningful brands, and helping people improve their lives.",
    ],
  },
  {
    num: "07",
    title: "Entrepreneurship and the Future",
    paragraphs: [
      "Today, Miguel Santana continues pursuing a growing portfolio of ventures focused on media, technology, business, finance, sports, and personal development.",
      "His long-term vision is rooted in a simple belief:",
    ],
    pullQuote: "The future belongs to people willing to keep building even when progress is slow.",
    afterQuote: [
      "Through podcasts, writing, business development, and creative projects, he hopes to inspire others to pursue their own goals regardless of where they started or how many obstacles stand in the way.",
    ],
  },
  {
    num: "08",
    title: "Today",
    paragraphs: [
      "Today, Miguel Santana remains committed to growth, learning, entrepreneurship, and creating a better future.",
      "His mission is not simply to build businesses.",
      "It is to build ideas, platforms, and opportunities that encourage people to think bigger, pursue their goals, and recognize that setbacks do not define their future.",
    ],
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-20">
      <Seo
        title="About · Biography"
        description="From Santo Domingo to building MPR Ventures — the biography of Miguel Santana, Dominican-American entrepreneur, founder, and chief executive."
        path="/about"
        type="profile"
      />
      {/* Hero block */}
      <section className="px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-overline text-champagne mb-6">— Biography</p>
          <h1
            data-testid="about-title"
            className="font-display text-5xl md:text-7xl lg:text-[7.5rem] text-silver leading-[0.9] tracking-tighter max-w-6xl"
          >
            From Santo Domingo<br />
            <span className="italic font-light">to building the future.</span>
          </h1>

          <div className="mt-16 grid grid-cols-12 gap-8 md:gap-16">
            <div className="col-span-12 md:col-span-7">
              <p className="font-body text-xl md:text-2xl text-silver leading-relaxed max-w-3xl">
                Miguel Santana is a Dominican-American entrepreneur, media personality,
                aspiring author, and founder focused on business, personal growth, culture,
                sports, and the pursuit of opportunity.
              </p>
              <p className="mt-6 font-body text-lg text-silver-muted leading-relaxed max-w-3xl">
                Through his writing, podcasts, business ventures, and creative projects, he
                explores the intersection of ambition, identity, entrepreneurship, and modern
                society.
              </p>
            </div>

            <div className="col-span-12 md:col-span-5">
              <div className="grid grid-cols-2 gap-px bg-white/10 border hairline">
                <div className="bg-navy p-5">
                  <p className="font-overline text-silver-muted">Born</p>
                  <p className="font-display text-xl text-silver mt-2">Oct 1, 1987</p>
                  <p className="font-overline text-silver-muted mt-1">Santo Domingo, DR</p>
                </div>
                <div className="bg-navy p-5">
                  <p className="font-overline text-silver-muted">Founder</p>
                  <p className="font-display text-xl text-silver mt-2">MPR Ventures</p>
                  <p className="font-overline text-silver-muted mt-1">New York</p>
                </div>
                <div className="bg-navy p-5">
                  <p className="font-overline text-silver-muted">Heritage</p>
                  <p className="font-display text-xl text-silver mt-2">Dominican-American</p>
                </div>
                <div className="bg-navy p-5">
                  <p className="font-overline text-silver-muted">Disciplines</p>
                  <p className="font-display text-base text-silver mt-2 leading-tight">Business · Media · Sports · Culture</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portrait band */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <div className="relative aspect-[16/8] border hairline overflow-hidden">
            <img
              src={ASSETS.portrait2}
              alt="Miguel Santana"
              className="w-full h-full object-cover object-[center_20%]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-transparent to-[#050A15]/30" />
            <div className="absolute inset-0 grain" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 flex items-end justify-between">
              <div>
                <p className="font-overline text-champagne">Portrait · New York</p>
                <p className="font-display text-2xl md:text-3xl text-silver mt-2 italic">"Where a person begins does not determine where they can ultimately go."</p>
              </div>
              <p className="font-overline text-silver-muted hidden md:block">— Miguel Santana</p>
            </div>
          </div>
        </div>
      </section>

      {/* THE THREEE'S */}
      <div className="mt-24 md:mt-32">
        <Pillars />
      </div>

      {/* Chapters */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="font-overline text-champagne mb-4">— The Chapters</p>
              <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05]">
                A life in five<br />acts <span className="italic font-light text-champagne">— so far.</span>
              </h2>
            </div>
            <p className="font-body text-silver-muted max-w-md">
              The story of how a Dominican boy from Santo Domingo became the founder of a multi-city American conglomerate.
            </p>
          </div>

          <div className="border-y hairline">
            {CHAPTERS.map((c, i) => (
              <article
                key={c.num}
                data-testid={`chapter-${c.num}`}
                className="grid grid-cols-12 gap-6 md:gap-12 py-16 md:py-24 border-b hairline last:border-b-0"
              >
                <div className="col-span-12 md:col-span-4 lg:col-span-3">
                  <div className="md:sticky md:top-32">
                    <p className="font-display text-7xl md:text-8xl text-silver/15 tracking-tighter leading-none">{c.num}</p>
                    <h3 className="mt-4 font-display text-3xl md:text-4xl text-silver tracking-tight leading-tight">
                      {c.title}
                    </h3>
                    <div className="mt-6 h-px w-16 bg-champagne"></div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-8 lg:col-span-8 lg:col-start-5">
                  <div className="prose-luxe space-y-6 max-w-2xl">
                    {c.paragraphs.map((p, idx) => (
                      <p key={idx} className="font-body text-lg text-silver-muted leading-[1.85]">
                        {p}
                      </p>
                    ))}
                    {c.pullQuote && (
                      <blockquote className="mt-8 border-l-2 hairline-strong pl-6 font-display italic text-2xl md:text-3xl text-silver leading-[1.25]">
                        {c.pullQuote}
                      </blockquote>
                    )}
                    {c.afterQuote && c.afterQuote.map((p, idx) => (
                      <p key={`aq-${idx}`} className="font-body text-lg text-silver-muted leading-[1.85]">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Signature flourish — closes the biographical chapters */}
          <div className="mt-20 flex flex-col items-center text-center" data-testid="about-signature-flourish">
            <p className="font-overline text-silver-muted mb-6">— Signed</p>
            <img
              src={ASSETS.signature}
              alt="Miguel Santana signature"
              className="signature-img w-64 md:w-96 h-auto"
            />
            <p className="mt-6 font-overline text-silver-muted">Miguel Santana · Founder, MPR Ventures</p>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <div className="border hairline p-10 md:p-16 bg-navy-surface grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 md:col-span-8">
              <p className="font-overline text-champagne mb-4">— The Through-Line</p>
              <h2 className="font-display text-3xl md:text-5xl text-silver tracking-tighter leading-[1.05]">
                Setbacks become redirections.<br />
                <span className="italic font-light">Redirections become institutions.</span>
              </h2>
              <p className="mt-8 font-body text-lg text-silver-muted max-w-2xl leading-relaxed">
                Today, that journey is expressed through MPR Ventures and a portfolio of operating
                companies across five American cities. The chapters that started in Santo Domingo
                are still being written — now from boardrooms in New York, Miami, Los Angeles, Las
                Vegas, and Washington DC.
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 flex md:justify-end">
              <div className="flex flex-col gap-4">
                <Link to="/ventures" data-testid="about-cta-ventures" className="btn-editorial btn-editorial-filled">
                  Explore the Ventures
                </Link>
                <Link to="/contact" data-testid="about-cta-contact" className="btn-editorial">
                  Direct Inquiries
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
